import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Boxes,
  PackageOpen,
  ClipboardList,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printStockReport } from "@/lib/stockReportPrint";
import { displayItemName } from "@/lib/menuLocale";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const TABS = [
  { id: "condensed", label: "Condensed Stock", path: "/reports/condensed-stock", icon: Boxes },
  { id: "opening", label: "Opening Stock", path: "/reports/opening-stock", icon: PackageOpen },
  { id: "physical", label: "Physical Stock", path: "/reports/physical-stock", icon: ClipboardList },
];

const ENDPOINTS = {
  condensed: "/reports/stock/condensed",
  opening: "/reports/stock/opening",
  physical: "/reports/stock/physical",
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function StockReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const activeTab = useMemo(() => {
    if (location.pathname.includes("opening-stock")) return "opening";
    if (location.pathname.includes("physical-stock")) return "physical";
    return "condensed";
  }, [location.pathname]);

  const isRange = activeTab === "condensed";
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [singleDate, setSingleDate] = useState(today);
  const [physicalLines, setPhysicalLines] = useState([]);

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["stock-report", activeTab, branchId, fromDate, toDate, singleDate],
    queryFn: async () => {
      if (activeTab === "condensed") {
        const { data: res } = await api.get(ENDPOINTS.condensed, {
          params: { ...reportParams, from_date: fromDate, to_date: toDate },
        });
        return res;
      }
      if (activeTab === "opening") {
        const { data: res } = await api.get(ENDPOINTS.opening, {
          params: { ...reportParams, as_of_date: singleDate },
        });
        return res;
      }
      const { data: res } = await api.get(ENDPOINTS.physical, {
        params: { ...reportParams, count_date: singleDate },
      });
      return res;
    },
    enabled: Boolean(canQuery && (isRange ? fromDate && toDate : singleDate)),
  });

  useEffect(() => {
    if (activeTab === "physical" && report?.rows) {
      setPhysicalLines(
        report.rows.map((r) => ({
          menu_item_id: r.menu_item_id,
          item_code: r.item_code,
          item_name: r.item_name,
          book_qty: r.book_qty,
          physical_qty: r.physical_qty ?? "",
        })),
      );
    }
  }, [activeTab, report?.rows]);

  const savePhysicalMutation = useMutation({
    mutationFn: async () => {
      const lines = physicalLines
        .filter((l) => l.physical_qty !== "" && l.physical_qty != null)
        .map((l) => ({
          menu_item_id: l.menu_item_id,
          physical_qty: Number(l.physical_qty),
        }));
      const { data: res } = await api.post("/stock/physical-counts", {
        branch_id: branchId,
        count_date: singleDate,
        lines,
      });
      return res;
    },
    onSuccess: () => {
      toast.success("Physical stock count saved.");
      queryClient.invalidateQueries({ queryKey: ["stock-report"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to save physical count.");
    },
  });

  const title = TABS.find((t) => t.id === activeTab)?.label ?? "Stock Reports";

  const updatePhysicalQty = (menuItemId, value) => {
    setPhysicalLines((prev) =>
      prev.map((l) =>
        l.menu_item_id === menuItemId ? { ...l, physical_qty: value } : l,
      ),
    );
  };

  return (
    <div className="min-w-0">
      <PageHeader
        title={title}
        subtitle={
          activeTab === "condensed"
            ? "Opening, movements and closing stock by item"
            : activeTab === "opening"
              ? "Opening balances as of the selected date"
              : "Book stock vs physical count with variance"
        }
        actions={
          <ReportExportButtons
            disabled={!report}
            title={title}
            filename={`stock-${activeTab}`}
            onPrint={() => printStockReport(report, activeTab, { branchId, allBranches })}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="flex flex-wrap items-end gap-4">
          {isRange ? (
            <>
              <div>
                <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
                <Input type="date" className="h-9 w-[160px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
                <Input type="date" className="h-9 w-[160px]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Date</Label>
              <Input type="date" className="h-9 w-[160px]" value={singleDate} onChange={(e) => setSingleDate(e.target.value)} />
            </div>
          )}
          {(isLoading || isFetching) && (
            <Pill className="mb-1"><Loader2 size={12} className="animate-spin mr-1" /> Loading…</Pill>
          )}
          {activeTab === "physical" && (
            <Button
              size="sm"
              className="h-9"
              onClick={() => savePhysicalMutation.mutate()}
              disabled={savePhysicalMutation.isPending || allBranches || !branchId}
            >
              {savePhysicalMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <><Save size={14} className="mr-1.5" /> Save counts</>
              )}
            </Button>
          )}
        </div>
      </Card>

      {report && activeTab === "condensed" && <CondensedView report={report} />}
      {report && activeTab === "opening" && <OpeningView report={report} />}
      {report && activeTab === "physical" && (
        <PhysicalView
          report={report}
          physicalLines={physicalLines}
          onPhysicalChange={updatePhysicalQty}
        />
      )}
    </div>
  );
}

function SummaryRow({ summary, type }) {
  const items =
    type === "condensed"
      ? [
          { label: "Items", value: num(summary.item_count) },
          { label: "Opening qty", value: num(summary.opening_qty) },
          { label: "Stock in", value: num(summary.stock_in) },
          { label: "Stock out", value: num(summary.stock_out) },
          { label: "Closing qty", value: num(summary.closing_qty) },
          { label: "Closing value", value: aed(summary.closing_value), emphasize: true },
        ]
      : type === "opening"
        ? [
            { label: "Items", value: num(summary.item_count) },
            { label: "Total qty", value: num(summary.total_qty) },
            { label: "Total value", value: aed(summary.total_value), emphasize: true },
          ]
        : [
            { label: "Items", value: num(summary.item_count) },
            { label: "Counted", value: num(summary.counted_items) },
            { label: "Book qty", value: num(summary.total_book_qty) },
            { label: "Physical qty", value: num(summary.total_physical_qty) },
            { label: "Variance qty", value: num(summary.total_variance_qty) },
            { label: "Variance value", value: aed(summary.total_variance_value), emphasize: true },
          ];

  return <ReportKpiStrip items={items} />;
}

function CondensedView({ report }) {
  return (
    <>
      <SummaryRow summary={report.summary} type="condensed" />
      <ReportTableCard title="Stock by item">
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">Item</th>
              <th className="text-left">Unit</th>
              <th className="text-right">Opening</th>
              <th className="text-right">In</th>
              <th className="text-right">Out</th>
              <th className="text-right">Closing</th>
              <th className="text-right">Cost</th>
              <th className="text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r) => (
              <tr
                key={r.menu_item_id}
                className={cn(r.below_reorder && "bg-amber-50/60")}
              >
                <td className="font-mono text-[12px]">{r.item_code}</td>
                <td>{displayItemName(r)}</td>
                <td>{r.unit}</td>
                <td className="text-right">{num(r.opening_qty)}</td>
                <td className="text-right text-green-700">{num(r.stock_in)}</td>
                <td className="text-right text-red-700">{num(r.stock_out)}</td>
                <td className="text-right font-medium">{num(r.closing_qty)}</td>
                <td className="text-right font-mono">{aed(r.cost_per_unit)}</td>
                <td className="text-right font-mono font-medium">{aed(r.closing_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function OpeningView({ report }) {
  return (
    <>
      <SummaryRow summary={report.summary} type="opening" />
      <ReportTableCard title="Opening balances">
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">Item</th>
              <th className="text-left">Unit</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Cost</th>
              <th className="text-right">Value</th>
              <th className="text-left">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r) => (
              <tr key={`${r.menu_item_id}-${r.effective_date}`}>
                <td className="font-mono text-[12px]">{r.item_code}</td>
                <td>{displayItemName(r)}</td>
                <td>{r.unit}</td>
                <td className="text-right font-medium">{num(r.opening_qty)}</td>
                <td className="text-right font-mono">{aed(r.cost_per_unit)}</td>
                <td className="text-right font-mono font-medium">{aed(r.opening_value)}</td>
                <td className="text-slate-500 text-[12px]">{r.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function PhysicalView({ report, physicalLines, onPhysicalChange }) {
  return (
    <>
      <SummaryRow summary={report.summary} type="physical" />
      <ReportTableCard title="Physical stock count">
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">Item</th>
              <th className="text-right">Book</th>
              <th className="text-right">Physical</th>
              <th className="text-right">Variance</th>
              <th className="text-right">Var. value</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r) => {
              const line = physicalLines.find((l) => l.menu_item_id === r.menu_item_id);
              const physicalVal = line?.physical_qty;
              const variance =
                physicalVal !== "" && physicalVal != null
                  ? Number(physicalVal) - r.book_qty
                  : r.variance_qty;

              return (
                <tr key={r.menu_item_id}>
                  <td className="font-mono text-[12px]">{r.item_code}</td>
                  <td>{displayItemName(r)}</td>
                  <td className="text-right">{num(r.book_qty)}</td>
                  <td className="text-right">
                    <Input
                      type="number"
                      min={0}
                      step="0.001"
                      className="h-8 w-24 ml-auto text-right"
                      value={line?.physical_qty ?? ""}
                      onChange={(e) => onPhysicalChange(r.menu_item_id, e.target.value)}
                    />
                  </td>
                  <td
                    className={cn(
                      "text-right font-medium",
                      variance != null && variance !== 0 && "text-amber-700",
                    )}
                  >
                    {variance != null ? num(variance) : "—"}
                  </td>
                  <td className="text-right font-mono">
                    {variance != null ? aed(variance * r.cost_per_unit) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}
